// แผนที่ถิ่นที่อยู่ + ตำแหน่งของเรา + แถบความสูง ใช้ในหน้าการ์ด หน้าใช้ชีวิต และหน้าสรุป (docs/PLAN.md ข้อ 4.4)

import { migrantOf } from "../../data/migrants";
import { migrationStatus } from "../../engine/life";
import type { LifeState, Neighbor } from "../../engine/types";
import type { GeoTrack } from "../../geo/locate";
import { MONTH_TH, flywayLines, groundKind, groundName, migrationAtBirth, monthAt } from "../../geo/migration";
import { altitudeText, countryName, formatCoords, placeLabel, placeName } from "../../geo/place";
import { realmOf, type SpeciesRange } from "../../geo/range";
import type { SpeciesCard } from "../../shared/api";
import type { GeoState } from "../geo";
import { fmt } from "../names";
import { AltitudeBar } from "./AltitudeBar";
import { WorldMap } from "./WorldMap";

const level = (m: number) => (m >= 0 ? `+${fmt(Math.round(m))}` : `−${fmt(Math.round(-m))}`);
const altitudeRange = (low: number, high: number) => `${level(low)} ถึง ${level(high)} ม. จากระดับน้ำทะเล`;

interface Props {
  geo: GeoState;
  card: SpeciesCard;
  range: SpeciesRange | null;
  track: GeoTrack | null;
  mode: "birth" | "now" | "life";
  neighbors?: { predators: Neighbor[]; prey: Neighbor[] }; // นักล่าและเหยื่อชนิดจริงแถวนี้
  life?: LifeState; // ใช้บอกเดือนและแหล่งที่ควรอยู่ของสัตว์อพยพ
}

/** เส้นทางอพยพของประชากรที่เราเกิด เดือนปัจจุบัน และฤดูนี้ควรอยู่ที่ไหน */
function migrationOf(card: SpeciesCard, track: GeoTrack, life?: LifeState) {
  const migrant = migrantOf(card.species.sci);
  const state = life?.migration ?? (life ? undefined : migrationAtBirth(card.species, track.at));
  const route = state && migrant?.routes[state.route];
  if (!migrant || !state || !route) return null;
  const now = life?.alive ? migrationStatus(life) : null;
  const month = monthAt(state, life?.ageDays ?? 0);
  return { migrant, route, month, target: now?.target ?? null };
}

const names = (list: readonly Neighbor[]) => list.map((n) => n.nameTh ?? n.sci).join(" · ");

export function GeoPanel({ geo, card, range, track, mode, neighbors, life }: Props) {
  if (geo.status === "loading") return <p className="small muted">กำลังโหลดแผนที่…</p>;
  if (geo.status === "error") return <p className="small muted">โหลดแผนที่ไม่ได้ แต่ยังเล่นต่อได้ตามปกติ</p>;
  if (!range || !track) return <p className="small muted">ยังไม่มีข้อมูลถิ่นที่อยู่ของชนิดนี้ จึงยังแสดงแผนที่ไม่ได้</p>;

  const { world, grid } = geo.data;
  const here = placeName(grid, track.cell, track.onSea, track.at);
  const hereFull = placeLabel(grid, track);
  const born = placeName(grid, track.birth.cell, track.birth.onSea, track.path[0]);
  const countries = card.countries.map(countryName);
  const migration = migrationOf(card, track, life);

  return (
    <section className="card geo">
      {mode === "birth" && (
        <p>
          <span className="muted small">จุดเกิดของคุณ</span>
          <br />
          <b>{hereFull}</b> <span className="small muted">{formatCoords(track.at)}</span>
        </p>
      )}
      {mode === "now" && (
        <p>
          <span className="muted small">ตำแหน่งปัจจุบัน</span>
          <br />
          <b>{hereFull}</b> <span className="small muted">{formatCoords(track.at)}</span>
        </p>
      )}
      {mode === "life" && (
        <p>
          <span className="muted small">เดินทางรวมประมาณ</span> <b>{fmt(Math.round(track.km))} กม.</b>
          <br />
          <span className="small">
            เกิดที่{born}
            {track.cell !== track.birth.cell ? ` · จากไปที่${here}` : " · ใช้ชีวิตอยู่แถวนั้นจนจบ"}
          </span>
        </p>
      )}

      <WorldMap
        world={world}
        range={range}
        path={mode === "birth" ? [track.at] : track.path}
        realm={realmOf(card.species.traits)}
        grounds={migration ? { a: migration.route.a, b: migration.route.b, flyways: flywayLines(migration.route) } : undefined}
        label={`แผนที่ถิ่นที่อยู่ของ${card.species.nameTh ?? card.species.sci} และ${mode === "birth" ? "จุดเกิด" : "ตำแหน่ง"}ของคุณที่${here}`}
      />

      <div className="geo-detail">
        <AltitudeBar altitude={track} />
        <div className="geo-notes">
          <p>{altitudeText(track)}</p>
          {mode === "life" && track.highest !== track.lowest && (
            <p className="small muted">
              ตลอดชีวิตอยู่ระหว่าง {altitudeRange(track.lowest, track.highest)}
            </p>
          )}
          <ul className="legend small muted">
            <li>
              <span className="swatch range" /> {range.source === "records" ? "ถิ่นที่อยู่ (สีเข้ม = พบบ่อย)" : "ถิ่นที่อยู่โดยประมาณ"}
            </li>
            <li>
              <span className="swatch here" /> {mode === "birth" ? "จุดเกิด" : "คุณ"}
            </li>
            {migration && (
              <>
                <li>
                  <span className="swatch ground-a" /> {groundKind(migration.migrant, "a")}
                </li>
                <li>
                  <span className="swatch ground-b" /> {groundKind(migration.migrant, "b")}
                </li>
                {migration.route.via && (
                  <li>
                    <span className="swatch flyway" /> เส้นทางอพยพ
                  </li>
                )}
              </>
            )}
            {mode !== "birth" && track.path.length > 1 && (
              <li>
                <span className="swatch route" /> เส้นทางที่เดินทางมา
              </li>
            )}
          </ul>
          {range.source === "countries" && countries.length > 0 && (
            <p className="small muted">
              ประมาณจากประเทศที่พบ: {countries.slice(0, 8).join(" ")}
              {countries.length > 8 ? " …" : ""}
            </p>
          )}
        </div>
      </div>
      {migration && (
        <p className="migration small">
          <span className="muted">สัตว์อพยพ</span> {groundKind(migration.migrant, "a")}ที่{migration.route.aNameTh} ⇄ {groundKind(migration.migrant, "b")}ที่
          {migration.route.bNameTh}
          {mode !== "life" && (
            <>
              <br />
              <span className="muted">{mode === "birth" ? "เกิดเดือน" : "ตอนนี้เดือน"}</span>
              {MONTH_TH[migration.month - 1]}
              {migration.target && ` · ฤดูนี้ควรอยู่ที่${groundName(migration.route, migration.target)}`}
            </>
          )}
        </p>
      )}
      {neighbors && (neighbors.predators.length > 0 || neighbors.prey.length > 0) && (
        <div className="wildlife small">
          {neighbors.predators.length > 0 && (
            <p>
              <span className="muted">{mode === "birth" ? "นักล่าของตัวเต็มวัยแถวนี้" : "นักล่าแถวนี้"}</span> {names(neighbors.predators)}
            </p>
          )}
          {neighbors.prey.length > 0 && (
            <p>
              <span className="muted">{mode === "birth" ? "อาหารของตัวเต็มวัยแถวนี้" : "อาหารแถวนี้"}</span> {names(neighbors.prey)}
            </p>
          )}
        </div>
      )}
      <p className="credit">
        {range.source === "records" ? "ข้อมูลการพบ: GBIF.org · " : ""}แผนที่: Natural Earth · ประเภทพื้นที่: RESOLVE Ecoregions 2017 (CC-BY 4.0) · ความสูงและความลึก: NOAA ETOPO
      </p>
    </section>
  );
}
